import { type Container, Graphics, type Sprite } from 'pixi.js';
import type { ClassDrawing, PaintedDrawing } from '../drawings';
import { type Shape, TAU, paintSheet } from '../paint';
import { type Look, angleSpring, angleTo } from '../player-look';
import { REFERENCE_RADIUS } from '../textures-looks';
import { add, hide, placeOutline, setTint } from '../util';
import { HAIR_POINTS, HAIR_SHAPE, hairMass, hairSide, hairStrand, hairWave } from './babacool-hair';
import { BABACOOL_PIECES, HAIR, HANDS, STRUM, SUNFLOWERS, UNIT } from './babacool-paint';

type Parts = Readonly<Record<keyof typeof BABACOOL_PIECES, Shape>>;

const EXTENT = 40;
const MIDDLE = -14;

const HAIR_MOTION = {
  normal: { beats: 4, wave: 1 },
  calm: { beats: 8, wave: 0.5 },
} as const;
const HAIR_SPRING = { rate: 0.16, damping: 0.75, lean: 0.9, stretch: 0.12 } as const;
const OUTLINE = 2;
const DARK = 0x10080a;
const EDGE = 0.7;
const STRAND = 0.55;
const STRUM_BEATS = 0.5;

function createBabaLook(bodies: Container, parts: Parts): Look {
  const hairOutline = bodies.addChild(new Graphics());
  const hair = bodies.addChild(new Graphics());
  const sunflowers = SUNFLOWERS.map(() => add(bodies, parts.sunflower));
  const outline = add(bodies, parts.silhouette);
  const torso = add(bodies, parts.torso);
  const necklace = add(bodies, parts.necklace);
  const guitar = add(bodies, parts.guitar);
  const strumming = add(bodies, parts.hand);
  const fretting = add(bodies, parts.hand);
  const head = add(bodies, parts.head);
  const crown = add(bodies, parts.crown);
  const body = [torso, necklace, guitar, strumming, fretting, head, crown, ...sunflowers];
  const swing = new Float64Array(2);
  const mass: number[] = new Array<number>(HAIR_POINTS * 2).fill(0);
  // Pixi keeps the points of a path until it draws it: each strand has its own.
  const strands = Array.from({ length: HAIR_SHAPE.strands }, () =>
    new Array<number>((HAIR_SHAPE.steps + 1) * 2).fill(0),
  );
  const sketch: number[] = new Array<number>(HAIR_POINTS * 2).fill(0);
  let fresh = true;
  let originX = 0;
  let originY = 0;
  let cos = 1;
  let sin = 0;
  let size = 1;
  let facing = 0;

  function put(sprite: Sprite, along: number, across: number): void {
    sprite.position.set(
      originX + (along * cos - across * sin) * UNIT * size,
      originY + (along * sin + across * cos) * UNIT * size,
    );
    sprite.rotation = facing;
    sprite.scale.set(size);
  }

  return {
    reset() {
      fresh = true;
    },
    hide() {
      hide(...body, outline);
      hair.visible = hairOutline.visible = false;
    },
    place(input) {
      const { x, y, angle, scale, color, frame } = input;
      const { now, calm, light, tempo } = frame;
      const beats = (now - tempo.originTick) / tempo.ticksPerBeat;
      originX = x;
      originY = y;
      cos = Math.cos(angle);
      sin = Math.sin(angle);
      size = scale;
      facing = angle;
      for (const sprite of body) {
        sprite.visible = true;
      }

      const lean = Math.max(
        -HAIR_SPRING.lean,
        Math.min(HAIR_SPRING.lean, angleTo(angle, input.heading) * input.moving),
      );
      const hang = angle + lean;
      if (fresh) {
        swing[0] = hang;
        swing[1] = 0;
        fresh = false;
      }
      angleSpring(
        swing,
        0,
        hang,
        HAIR_SPRING.rate * HAIR_SPRING.rate,
        2 * HAIR_SPRING.damping * HAIR_SPRING.rate,
        input.dt,
      );
      const motion = calm ? HAIR_MOTION.calm : HAIR_MOTION.normal;
      const phase = (TAU * beats) / motion.beats;
      const hairCos = Math.cos(swing[0] ?? angle);
      const hairSin = Math.sin(swing[0] ?? angle);
      const unit = UNIT * scale;
      const length = HAIR_SHAPE.length * (1 + HAIR_SPRING.stretch * input.moving);
      const at = (along: number, across: number): [number, number] => [
        x + (along * hairCos - across * hairSin) * unit,
        y + (along * hairSin + across * hairCos) * unit,
      ];
      const toWorld = (from: readonly number[], into: number[], count: number): void => {
        for (let index = 0; index < count; index += 1) {
          const [wx, wy] = at(from[index * 2] ?? 0, from[index * 2 + 1] ?? 0);
          into[index * 2] = wx;
          into[index * 2 + 1] = wy;
        }
      };
      hairMass(sketch, length, phase, motion.wave);
      toWorld(sketch, mass, HAIR_POINTS);
      hair.visible = true;
      hairOutline.visible = !light.additive;
      hair.clear();
      hairOutline.clear();
      if (hairOutline.visible) {
        hairOutline
          .poly(mass)
          .fill(frame.palette.texte)
          .stroke({ width: EDGE * unit + OUTLINE, color: frame.palette.texte, join: 'round' });
      }
      hair
        .poly(mass)
        .fill(HAIR.mass)
        .stroke({ width: EDGE * unit, color: DARK, join: 'round' });
      for (let index = 0; index < HAIR_SHAPE.strands; index += 1) {
        const offset = (2 * index) / (HAIR_SHAPE.strands - 1) - 1;
        const strand = strands[index] ?? [];
        hairStrand(sketch, offset, length, phase, motion.wave);
        toWorld(sketch, strand, HAIR_SHAPE.steps + 1);
        hair.poly(strand, false).stroke({
          width: STRAND * unit,
          color: HAIR.strands[index % HAIR.strands.length] ?? HAIR.mass,
          cap: 'round',
          join: 'round',
        });
      }
      SUNFLOWERS.forEach((flower, index) => {
        const sprite = sunflowers[index];
        if (sprite === undefined) {
          return;
        }
        const [fx, fy] = at(
          HAIR_SHAPE.root - flower.along * length,
          flower.side * 0.6 * hairSide(flower.along) +
            hairWave(flower.along, 0.25 * (flower.side + 1), phase, motion.wave),
        );
        sprite.position.set(fx, fy);
        sprite.rotation = facing + index * 0.4;
        sprite.scale.set(flower.size * scale);
        setTint(sprite, 0xffffff);
      });

      put(torso, 0, 0);
      placeOutline(outline, torso, parts.silhouette.texture, REFERENCE_RADIUS, frame);
      setTint(torso, color);
      put(necklace, 0, 0);
      setTint(necklace, 0xffffff);
      put(guitar, 0, 0);
      setTint(guitar, 0xffffff);
      const chord = input.fireCount > 0 ? input.sinceFire / tempo.ticksPerBeat : Infinity;
      const strum =
        chord < STRUM_BEATS ? Math.sin((Math.PI * chord) / STRUM_BEATS) * (calm ? 0.5 : 1) : 0;
      put(strumming, HANDS.strum[0], HANDS.strum[1] + STRUM * strum);
      setTint(strumming, input.teint);
      put(fretting, HANDS.fret[0], HANDS.fret[1]);
      setTint(fretting, input.teint);
      put(head, 0, 0);
      setTint(head, input.teint);
      put(crown, 0, 0);
      setTint(crown, 0xffffff);
      return 0;
    },
  };
}

export const BABACOOL_DRAWING: ClassDrawing = {
  classId: 'babacool',
  paint(density): PaintedDrawing {
    const { shapes, source } = paintSheet(BABACOOL_PIECES, density);
    return {
      extent: EXTENT,
      height: 1,
      centerAhead: MIDDLE,
      shadow: shapes.shadow,
      source,
      create: (bodies: Container) => createBabaLook(bodies, shapes),
    };
  },
};
